import { useEffect, useState } from "react";
import Collapsible from "react-collapsible";
import { CartConfigType, PartsType, ProductType, RealtimeData, RequestQuotePayloadType } from "../types/types";
import { AddToCartButton } from "./AddToCartButton";
import { Badge } from "./ProductCard";
import MyImage from "../utils/MyImage";
import { useTranslation } from "react-i18next";

type PropsType = {
  parts: PartsType[];
  hasMonthlySubscription: boolean;
  showProductImages: boolean;
  shopShowExternalStock: boolean;
  shopAutoCreateProduct: boolean;
  translations: Record<string, string>;
  cartConfig: CartConfigType;
  onAddToCart: (part: PartsType | undefined, realtimeData: RealtimeData | undefined, variantId: string | undefined, quantity: string) => void;
  onClickTitle: (part: RealtimeData) => void;
  onClickImage: (imageUrl: string) => void;
  onHoverPartItem: (part: PartsType | null) => void;
  onClickRequestQuote: (payload: RequestQuotePayloadType) => void;
};

type AftermarketProps = {
  part: RealtimeData;
  hasMonthlySubscription: boolean;
  showProductImages: boolean;
  addingToCartId: string | null;
  cachedQuantity: string | undefined;
  productType: ProductType;
  shopAutoCreateProduct: boolean;
  externalStock?: number;
  shopShowExternalStock: boolean;
  translations: Record<string, string>;
  cartConfig: CartConfigType;
  onClickTitle: () => void;
  setAddingToCartSpinner: (isLoading: boolean) => void;
  onAddToCart: () => void;
  onQuantityChanged: (newValue: number | undefined) => void;
  onClickImage: (imageUrl: string) => void;
  onClickRequestQuote: (payload: RequestQuotePayloadType) => void;
};

const BORDER_COLOR = "#d4d4d4";
const borderStyle = (topBorder: boolean = false) => {
  return {
    borderLeft: `1px solid ${BORDER_COLOR}`,
    borderRight: `1px solid ${BORDER_COLOR}`,
    borderBottom: `1px solid ${BORDER_COLOR}`,
    borderTop: topBorder ? `1px solid ${BORDER_COLOR}` : undefined,
  };
};

export const PartsList = ({
  parts,
  showProductImages,
  hasMonthlySubscription,
  shopAutoCreateProduct,
  shopShowExternalStock,
  translations,
  cartConfig,
  onAddToCart,
  onClickTitle,
  onClickImage,
  onHoverPartItem,
  onClickRequestQuote,
}: PropsType) => {
  const { t } = useTranslation();
  const [quantityMap, setQuantityMap] = useState<{ [x: string]: number | undefined }>({});
  const [addingToCartId, setAddingToCartId] = useState<string | null>(null);
  const [openedAftermarketMap, setOpenAftermarketMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setQuantityMap(
      parts.reduce((acc, part) => {
        const apiQuantity = (part.quantity && part.quantity === "0" ? 1 : parseInt(part.quantity)) || 1;

        acc[part.id] = apiQuantity;

        part?.powerparts?.forEach((powerpart) => {
          acc[powerpart.sku] = 1;
        });
        part?.aftermarkets?.forEach((aftermarket) => {
          acc[aftermarket.sku] = 1;
        });
        part?.similarOemParts?.forEach((similarOemPart) => {
          acc[similarOemPart.sku] = 1;
        });

        return acc;
      }, {} as { [x: string]: number })
    );

    // open dropdown if part has no stock but has aftermarkets
    const newMap = { ...openedAftermarketMap };
    parts.forEach((part) => {
      if (!part?.realtimeData || part?.realtimeData?.inventoryQuantity === 0) {
        newMap[part.id] = true;
      }
    });
    setOpenAftermarketMap(newMap);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts]);

  return (
    <div className="w-full">
      <div style={{ ...borderStyle(true) }}>
        <div className="flex items-center px-2 gap-2">
          <div className="grow"></div>
          <div>{t("price")}</div>
          <div>{t("quantity")}</div>
          <div>{t("add_cart")}</div>
        </div>
      </div>

      {parts.map((p, index) => {
        const realtimeData: RealtimeData | undefined = p.realtimeData;

        const cachedQuantity = quantityMap[p.id] ? quantityMap[p.id] + "" : undefined;

        const partPrice = realtimeData?.contextualPricing?.price?.amount || p.price.toFixed(2) || undefined;
        const currencyCode = realtimeData?.contextualPricing?.price?.currencyCode || p.currency;
        const realtimeCompareAtPrice = realtimeData?.contextualPricing?.compareAtPrice?.amount;

        const variantId = p.realtimeData?.variantId;
        const availableForSale = realtimeData?.availableForSale;
        const canSellWithoutStock = realtimeData?.inventoryPolicy === "CONTINUE";
        const shouldAutoCreateProduct = shopAutoCreateProduct && (shopShowExternalStock ? p.externalStock && p.externalStock > 0 : false);
        const hasAddToCard = shouldAutoCreateProduct || (p.realtimeData && hasMonthlySubscription && availableForSale);
        const outOfStock = p.realtimeData && !availableForSale;
        const inventoryQuantity = realtimeData?.inventoryQuantity || 0;
        const inStock = inventoryQuantity > 0;
        const inStockAtExternal = p.externalStock && p.externalStock > 0;
        const isReplaced = (p.replacements && Object.keys(p.replacements)?.length > 0) || (p.replaced_by && Object.keys(p.replaced_by)?.length > 0);
        const isReplacement = p.replacement;
        const hasAftermarkets = p?.aftermarkets && p?.aftermarkets.length > 0;
        const hasPowerparts = p?.powerparts && p?.powerparts.length > 0;
        const hasSimilarOemParts = p?.similarOemParts && p?.similarOemParts.length > 0;

        const textDecorationColor = isReplaced ? "black" : undefined;
        const title = p.realtimeData?.title || p.name;

        return (
          <div
            key={p.id + "_" + index}
            className={(isReplacement ? "ms-2" : "") + "hover:bg-gray-100"}
            onMouseEnter={() => onHoverPartItem(p)}
            onMouseLeave={() => onHoverPartItem(null)}
          >
            <div style={{ ...borderStyle(), borderBottom: hasAftermarkets || hasPowerparts || hasSimilarOemParts ? "none" : `1px solid ${BORDER_COLOR}` }}>
              <div className="flex items-center px-1 gap-2 pt-1 pb-1">
                <div style={{ minWidth: 12 }}>
                  <p className="lg:text-xxs text-xxxs text-black">{p.no}</p>
                </div>

                {showProductImages && (p.realtimeData?.image || p.image) ? (
                  <MyImage
                    src={p.realtimeData?.image || p.image}
                    alt={""}
                    className="lg:w-14 lg:h-14 w-11 h-11 rounded-lg bg-white object-scale-down cursor-pointer shadow-md"
                    onClick={() => {
                      const image = p.realtimeData?.image || p.image;
                      if (image) {
                        onClickImage(image);
                      }
                    }}
                  />
                ) : (
                  showProductImages && <div className="lg:w-14 lg:h-14 w-11 h-11"></div>
                )}

                <div className="grow" style={{ textDecoration: isReplaced && !inStock ? "line-through" : undefined, textDecorationColor }}>
                  {variantId ? (
                    <div className="flex items-center gap-1">
                      <p
                        className="lg:text-sm text-xs font-medium text-blue-600 dark:text-blue-500 hover:underline cursor-pointer"
                        onClick={() => {
                          if (p.realtimeData) {
                            onClickTitle(p.realtimeData);
                          }
                        }}
                      >
                        {title}
                      </p>
                    </div>
                  ) : (
                    <strong className="lg:text-sm text-xs font-medium text-black">{p.name}</strong>
                  )}
                  <div className="flex mt-1">
                    <div className="lg:text-xs text-xxs italic text-black">{p.id}</div>
                  </div>

                  {!inStock && shopShowExternalStock && inStockAtExternal ? (
                    <span className="text-xxs lg:text-xs text-center" style={{ fontStyle: "italic", color: cartConfig.cartExternalStockMessageColor }}>{`${cartConfig.cartExternalStockMessage}`}</span>
                  ) : null}
                </div>

                <div className="flex-none ">
                  {realtimeCompareAtPrice && (
                    <div>
                      <div className="lg:text-sm text-xs text-black" style={{ textDecoration: "line-through" }}>
                        {realtimeCompareAtPrice}
                      </div>
                    </div>
                  )}
                  <div>
                    <span className={`lg:text-sm text-xs text-black ${realtimeCompareAtPrice ? "text-red-800 font-bold" : "text-black"}`}>{partPrice}</span>
                    {currencyCode && <span className={`lg:text-xxs text-xxxs ${realtimeCompareAtPrice ? "text-red-800" : "text-black"}`}>{currencyCode}</span>}
                  </div>
                </div>

                <div className="flex-none" style={{ width: 44 }}>
                  {hasAddToCard && (
                    <input
                      value={cachedQuantity}
                      type="text"
                      pattern="[0-9]"
                      inputMode="numeric"
                      className=" bg-whiteno-spin w-full bg-transparent placeholder:text-slate-400 text-slate-700 text-sm border border-slate-200 rounded-md px-3 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-300 shadow-sm focus:shadow"
                      placeholder=""
                      autoComplete="off"
                      onChange={(e) => {
                        const newValue = e.target.value;
                        let valueInt;
                        if (newValue?.includes("-")) {
                          setQuantityMap({ ...quantityMap, [p.id]: undefined });
                        } else {
                          valueInt = parseInt(newValue);
                          if (valueInt < 1) {
                            valueInt = 1;
                          }
                          setQuantityMap({ ...quantityMap, [p.id]: valueInt });
                        }
                      }}
                    ></input>
                  )}
                </div>

                <div className="flex-none flex justify-center items-center" style={{ width: 55 }}>
                  {hasAddToCard ? (
                    <div className="text-center">
                      <AddToCartButton
                        disabled={p.id === addingToCartId}
                        loading={p.id === addingToCartId}
                        onClick={() => {
                          const canOrderMoreThanStock = canSellWithoutStock || shopAutoCreateProduct;

                          if (!canOrderMoreThanStock && cachedQuantity && parseInt(cachedQuantity) > inventoryQuantity) {
                            alert(`There are only ${inventoryQuantity} pieces in stock. You cannot order more than that`);
                            return;
                          }

                          if ((shopAutoCreateProduct || p.realtimeData) && cachedQuantity) {
                            onAddToCart(p, p.realtimeData, p.realtimeData?.variantId, cachedQuantity);
                            setAddingToCartId(p.id);
                            setTimeout(() => {
                              setAddingToCartId(null);
                            }, 2000);
                          }
                        }}
                        inStock={inStock}
                        cartConfig={cartConfig}
                      />

                      {inventoryQuantity > 0 ? (
                        <span style={{ fontSize: 10, fontStyle: "italic", color: "green" }}>{`${t("stock")}: ${inventoryQuantity}`}</span>
                      ) : null}
                    </div>
                  ) : outOfStock ? (
                    <div
                      onClick={() => {
                        onClickRequestQuote({
                          sku: p.id,
                          name: p.name,
                        });
                      }}
                    >
                      <div className="text-center ">
                        <span className="hover:bg-black hover:text-white inline-flex items-center rounded-md bg-gray-50 px-2 py-1 font-medium text-gray-600 ring-1 ring-gray-800/10 ring-inset lg:text-xxs text-xxxs cursor-pointer">
                          {translations.request_quote || t("request_quote")}
                        </span>
                        <span className="w-full text-xxxs font-bold">{t("out_of_stock")}</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <button
                        className="bg-transparent hover:bg-black hover:text-white text-black font-semibold py-1 px-1 border border-grey-500 hover:border-transparent rounded text-xxxs ring-1 ring-gray-800/10 ring-inset"
                        onClick={() => {
                          onClickRequestQuote({
                            sku: p.id,
                            name: p.name,
                          });
                        }}
                      >
                        {translations.request_quote || t("request_quote")}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {isReplaced && <span className="lg:text-sm text-xs ps-8 font-semibold text-black">{t("replaced_by")}</span>}
            </div>

            {(hasAftermarkets || hasPowerparts || hasSimilarOemParts) && (
              <div>
                <CollapsibleHeader
                  hasAftermarkets={hasAftermarkets || false}
                  hasPowerparts={hasPowerparts || false}
                  hasSimilarOemParts={hasSimilarOemParts || false}
                  openedAftermarketMap={openedAftermarketMap}
                  setOpenAftermarketMap={setOpenAftermarketMap}
                  pId={p.id}
                />

                <Collapsible triggerDisabled trigger={""} open={openedAftermarketMap[p.id] === true}>
                  {hasAftermarkets && (
                    <div className="pb-3 pt-1 bg-gray-200">
                      <div className="lg:ms-4 ms-1 w-full">
                        <Badge type={ProductType.AFTERMARKET} sufix={"⤵"} />
                      </div>
                      {p.aftermarkets?.map((aftermarketPart: RealtimeData) => {
                        const cachedQuantity = quantityMap[aftermarketPart.sku] ? quantityMap[aftermarketPart.sku] + "" : undefined;

                        return (
                          <AftermarketCard
                            key={aftermarketPart?.variantId}
                            part={aftermarketPart}
                            hasMonthlySubscription={hasMonthlySubscription}
                            showProductImages={showProductImages}
                            shopAutoCreateProduct={shopAutoCreateProduct}
                            onClickTitle={() => onClickTitle(aftermarketPart)}
                            setAddingToCartSpinner={(isLoading) => setAddingToCartId(isLoading ? aftermarketPart.sku : null)}
                            addingToCartId={addingToCartId}
                            onClickImage={onClickImage}
                            externalStock={p.externalStock}
                            shopShowExternalStock={shopShowExternalStock}
                            translations={translations}
                            cartConfig={cartConfig}
                            onAddToCart={() => {
                              const canSellWithoutStock = aftermarketPart.inventoryPolicy === "CONTINUE";
                              const canOrderMoreThanStock = canSellWithoutStock || shopAutoCreateProduct;

                              if (!canOrderMoreThanStock && cachedQuantity && parseInt(cachedQuantity) > aftermarketPart.inventoryQuantity) {
                                alert(`There are only ${aftermarketPart.inventoryQuantity} pieces in stock. You cannot order more than that`);
                                return;
                              }

                              if ((aftermarketPart && cachedQuantity) || shopAutoCreateProduct) {
                                onAddToCart(undefined, aftermarketPart, aftermarketPart.variantId, cachedQuantity || "1");
                              }
                            }}
                            cachedQuantity={cachedQuantity}
                            productType={ProductType.AFTERMARKET}
                            onQuantityChanged={(newValue: number | undefined) => {
                              setQuantityMap({ ...quantityMap, [aftermarketPart.sku]: newValue });
                            }}
                            onClickRequestQuote={onClickRequestQuote}
                          />
                        );
                      })}
                    </div>
                  )}

                  {hasPowerparts && (
                    <div className="pb-3 pt-1 bg-gray-200">
                      <div className="lg:ms-4 ms-1 w-full">
                        <Badge type={ProductType.POWERPARTS} sufix={"⤵"} />
                      </div>
                      {p.powerparts?.map((powerpart: RealtimeData) => {
                        const cachedQuantity = quantityMap[powerpart.sku] ? quantityMap[powerpart.sku] + "" : undefined;

                        return (
                          <AftermarketCard
                            key={powerpart.variantId}
                            part={powerpart}
                            hasMonthlySubscription={hasMonthlySubscription}
                            showProductImages={showProductImages}
                            shopAutoCreateProduct={shopAutoCreateProduct}
                            onClickTitle={() => onClickTitle(powerpart)}
                            onClickImage={onClickImage}
                            setAddingToCartSpinner={(isLoading) => setAddingToCartId(isLoading ? powerpart.sku : null)}
                            addingToCartId={addingToCartId}
                            externalStock={p.externalStock}
                            shopShowExternalStock={shopShowExternalStock}
                            translations={translations}
                            cartConfig={cartConfig}
                            onAddToCart={() => {
                              const canSellWithoutStock = powerpart.inventoryPolicy === "CONTINUE";
                              const canOrderMoreThanStock = canSellWithoutStock || shopAutoCreateProduct;

                              if (!canOrderMoreThanStock && cachedQuantity && parseInt(cachedQuantity) > powerpart.inventoryQuantity) {
                                alert(`There are only ${powerpart.inventoryQuantity} pieces in stock. You cannot order more than that`);
                                return;
                              }

                              if (powerpart && cachedQuantity) {
                                onAddToCart(undefined, powerpart, powerpart.variantId, cachedQuantity);
                              }
                            }}
                            cachedQuantity={cachedQuantity}
                            productType={ProductType.POWERPARTS}
                            onQuantityChanged={(newValue: number | undefined) => {
                              setQuantityMap({ ...quantityMap, [powerpart.sku]: newValue });
                            }}
                            onClickRequestQuote={onClickRequestQuote}
                          />
                        );
                      })}
                    </div>
                  )}

                  {hasSimilarOemParts && (
                    <div className="pb-3 pt-1 bg-gray-200">
                      <div className="lg:ms-4 ms-1 w-full">
                        <Badge type={ProductType.SIMILAR_OEM_PARTS} sufix={"⤵"} />
                      </div>
                      {p.similarOemParts?.map((similarOemPart: RealtimeData) => {
                        const cachedQuantity = quantityMap[similarOemPart.sku] ? quantityMap[similarOemPart.sku] + "" : undefined;

                        return (
                          <AftermarketCard
                            key={similarOemPart.variantId}
                            part={similarOemPart}
                            hasMonthlySubscription={hasMonthlySubscription}
                            showProductImages={showProductImages}
                            shopAutoCreateProduct={shopAutoCreateProduct}
                            onClickTitle={() => onClickTitle(similarOemPart)}
                            setAddingToCartSpinner={(isLoading) => setAddingToCartId(isLoading ? similarOemPart.sku : null)}
                            addingToCartId={addingToCartId}
                            onClickImage={onClickImage}
                            externalStock={p.externalStock}
                            shopShowExternalStock={shopShowExternalStock}
                            translations={translations}
                            cartConfig={cartConfig}
                            onAddToCart={() => {
                              const canSellWithoutStock = similarOemPart.inventoryPolicy === "CONTINUE";
                              const canOrderMoreThanStock = canSellWithoutStock || shopAutoCreateProduct;

                              if (!canOrderMoreThanStock && cachedQuantity && parseInt(cachedQuantity) > similarOemPart.inventoryQuantity) {
                                alert(`There are only ${similarOemPart.inventoryQuantity} pieces in stock. You cannot order more than that`);
                                return;
                              }

                              if (similarOemPart && cachedQuantity) {
                                onAddToCart(undefined, similarOemPart, similarOemPart.variantId, cachedQuantity || "1");
                              }
                            }}
                            cachedQuantity={cachedQuantity}
                            productType={ProductType.SIMILAR_OEM_PARTS}
                            onQuantityChanged={(newValue: number | undefined) => {
                              setQuantityMap({ ...quantityMap, [similarOemPart.sku]: newValue });
                            }}
                            onClickRequestQuote={onClickRequestQuote}
                          />
                        );
                      })}
                    </div>
                  )}
                </Collapsible>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const CollapsibleHeader = ({
  hasAftermarkets,
  hasPowerparts,
  hasSimilarOemParts,
  openedAftermarketMap,
  setOpenAftermarketMap,
  pId,
}: {
  hasAftermarkets: boolean;
  hasPowerparts: boolean;
  hasSimilarOemParts: boolean;
  openedAftermarketMap: { [x: string]: boolean };
  setOpenAftermarketMap: (map: { [x: string]: boolean }) => void;
  pId: string;
}) => {
  const { t } = useTranslation();

  const handleClick = () => {
    const newMap = { ...openedAftermarketMap };
    newMap[pId] = !openedAftermarketMap[pId];
    setOpenAftermarketMap(newMap);
  };

  return (
    <button
      className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-blue-50 active:bg-blue-100 transition-all duration-200 border-l border-r border-b border-gray-300 cursor-pointer shadow-sm hover:shadow-md transform hover:scale-[1.01] active:scale-[0.99]"
      style={{
        borderLeft: `1px solid ${BORDER_COLOR}`,
        borderRight: `1px solid ${BORDER_COLOR}`,
        borderBottom: `1px solid ${BORDER_COLOR}`,
        animationDuration: "1s",
      }}
      onClick={handleClick}
    >
      <div className="flex items-center gap-3">
        <div className="w-1 h-6 bg-blue-400 rounded-full"></div>
        <span className="lg:text-sm text-xs font-medium text-gray-800">
          {openedAftermarketMap[pId] ? t("hide_aftermarket_powerparts") : t("show_aftermarket_powerparts")}
        </span>
        <div className="flex gap-1">
          {hasAftermarkets && <span className="text-xxs bg-blue-200 text-blue-900 px-2 py-1 rounded-full font-medium">Aftermarket</span>}
          {hasPowerparts && <span className="text-xxs bg-yellow-200 text-yellow-900 px-2 py-1 rounded-full font-medium">Powerparts</span>}
          {hasSimilarOemParts && <span className="text-xxs bg-gray-200 text-gray-900 px-2 py-1 rounded-full font-medium">Similar OEM</span>}
        </div>
      </div>
      <svg
        className={`w-5 h-5 text-blue-600 transition-transform duration-200 ${openedAftermarketMap[pId] ? "rotate-180" : ""}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
};

const AftermarketCard = ({
  hasMonthlySubscription,
  showProductImages,
  part,
  externalStock,
  addingToCartId,
  cachedQuantity,
  shopAutoCreateProduct,
  shopShowExternalStock,
  translations,
  cartConfig,
  onClickTitle,
  setAddingToCartSpinner,
  onQuantityChanged,
  onAddToCart,
  onClickImage,
  onClickRequestQuote,
}: AftermarketProps) => {
  const { t } = useTranslation();
  const availableForSale = part?.availableForSale;
  const outOfStock = !part?.availableForSale;
  const inStock = part?.inventoryQuantity > 0;
  const hasAddToCard = shopAutoCreateProduct || (hasMonthlySubscription && availableForSale);

  const inventoryQuantity = part.inventoryQuantity || 0;
  const powerPartPrice = part.contextualPricing?.price?.amount;
  const currencyCode = part.contextualPricing?.price?.currencyCode;
  const realtimeCompareAtPrice = part.contextualPricing?.compareAtPrice?.amount;

  return (
    <div className="lg:ms-5 ms-2" style={{ ...borderStyle() }}>
      <div>
        <div className="flex items-center px-1 gap-1 pt-1 pb-1">
          {showProductImages && part?.image ? (
            <MyImage
              src={part?.image}
              alt={""}
              className="lg:w-14 lg:h-14 w-11 h-11 rounded-lg bg-white object-scale-down cursor-pointer shadow-md"
              onClick={() => onClickImage(part?.image)}
            />
          ) : (
            showProductImages && showProductImages && <div className="lg:w-14 lg:h-14 w-11 h-11"></div>
          )}

          <div className="grow relative pt-3 pb-3">
            <span className="font-medium text-blue-600 dark:text-blue-500 underline cursor-pointer" onClick={onClickTitle}>
              <span className="lg:text-sm text-xxs font-medium">{part?.title}</span>
            </span>

            <div className="flex">
              <div className="lg:text-xs text-xxs italic text-black">{part.sku}</div>
            </div>
          </div>

          <div className="flex-none">
            {realtimeCompareAtPrice && (
              <div>
                <span className="lg:text-sm text-xs text-black" style={{ textDecoration: "line-through" }}>
                  {realtimeCompareAtPrice}
                </span>
              </div>
            )}
            <div>
              <span className={`lg:text-sm text-xs text-black ${realtimeCompareAtPrice ? "text-red-800 font-bold" : "text-black"}`}>{powerPartPrice}</span>
              {currencyCode && <span className={`lg:text-xxs text-xxxs ${realtimeCompareAtPrice ? "text-red-800" : "text-black"}`}>{currencyCode}</span>}
            </div>
          </div>

          <div className="flex-none" style={{ width: 44 }}>
            {hasAddToCard && (
              <input
                value={cachedQuantity}
                type="text"
                pattern="[0-9]"
                inputMode="numeric"
                className="bg-white no-spin w-full bg-transparent placeholder:text-slate-400 text-slate-700 text-sm border border-slate-200 rounded-md px-3 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-300 shadow-sm focus:shadow"
                placeholder=""
                autoComplete="off"
                onChange={(e) => {
                  const newValue = e.target.value;
                  let valueInt;

                  if (newValue?.includes("-")) {
                    onQuantityChanged(undefined);
                  } else {
                    valueInt = parseInt(newValue);
                    if (valueInt < 1) {
                      valueInt = 1;
                    }
                    onQuantityChanged(valueInt);
                  }
                }}
              ></input>
            )}
          </div>

          <div className="flex-none flex justify-center items-center" style={{ width: 55 }}>
            {hasAddToCard ? (
              <div className="text-center">
                <AddToCartButton
                  disabled={part?.sku === addingToCartId}
                  loading={part?.sku === addingToCartId}
                  cartConfig={cartConfig}
                  onClick={() => {
                    onAddToCart();
                    setAddingToCartSpinner(true);
                    setTimeout(() => {
                      setAddingToCartSpinner(false);
                    }, 2000);
                  }}
                  inStock={inStock}
                />
                {inventoryQuantity > 0 ? (
                  <span style={{ fontSize: 10, fontStyle: "italic", color: "green" }}>{`${t("stock")}: ${inventoryQuantity}`}</span>
                ) : shopShowExternalStock && externalStock && externalStock > 0 ? (
                  <span
                    className="text-xxs lg:text-xs text-center"
                    style={{ fontSize: 9, fontStyle: "italic", color: cartConfig.cartExternalStockMessageColor }}
                  >{`${cartConfig.cartExternalStockMessage}`}</span>
                ) : null}
              </div>
            ) : outOfStock ? (
              <div
                className=""
                onClick={() =>
                  onClickRequestQuote({
                    sku: part.sku,
                    name: part.title || "",
                  })
                }
              >
                <div className="text-center ">
                  <span className="hover:bg-black hover:text-white inline-flex items-center rounded-md bg-gray-50 px-2 py-1 font-medium text-gray-600 ring-1 ring-gray-500/10 ring-inset lg:text-xxs text-xxxs cursor-pointer">
                    {translations.request_quote || t("request_quote")}
                  </span>
                  <span className="w-full text-xxxs font-bold">{t("out_of_stock")}</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
